// _landing — a pathless layout shared by /, /sign-in, and /get-started.
// It renders ONE persistent JoinLanding; the matched child path (below) only
// tells JoinLanding which inline module to open (none / sign-in / join). Because
// the layout stays mounted while navigating between its children, opening or
// closing a module changes the URL WITHOUT remounting the landing (no hero/video
// reload) — and browser back naturally closes the module.
//
// `?next=` is the page a signed-out visitor was bounced from (see
// `redirectToSignIn`); the sign-in module lands them back there. The layout owns
// it — not /sign-in — so the retain middleware keeps it while the visitor moves
// between the modules, and signed-in visitors are bounced straight to it (or to
// the feed when there is none).
//
// `/` also routes returning members to /sign-in, in its own route; its
// fail-closed session probe lives in the `_public` layout, so that error
// replaces the chrome too.

import { createFileRoute, Outlet, retainSearchParams } from '@tanstack/react-router';
import { z } from 'zod';
import { JoinLanding } from '@/components/landing';
import { returnPathSearchParam } from '@/lib/return-path';
import { redirectSignedInVisitor } from '@/lib/use-session';

const landingSearchSchema = z.object({
  next: returnPathSearchParam,
});

export const Route = createFileRoute('/_public/_landing')({
  validateSearch: landingSearchSchema.parse,
  search: { middlewares: [retainSearchParams(['next'])] },
  beforeLoad: ({ context, search }) => redirectSignedInVisitor(context.session, search.next),
  component: LandingLayout,
});

function LandingLayout() {
  return (
    <>
      <JoinLanding />
      {/* The child routes render nothing — they exist only to own the path. */}
      <Outlet />
    </>
  );
}
